import { projectsProjectAccessDecisionSchema } from '@crest/module-projects/contracts/project-access-decision';
import { projectsProjectReferenceSnapshotSchema } from '@crest/module-projects/contracts/project-reference';
import { z } from 'zod';
export const estimatingEstimatesCapability = 'estimating.estimates.v1';
export const estimatingEstimateVersionsCapability = 'estimating.estimate-versions.v1';
export const estimatingProjectEstimateSummariesCapability = 'estimating.project-estimate-summaries.v1';
export const estimatingEstimateIdSchema = z.string().uuid();
export const estimatingEstimateStatusSchema = z.enum([
    'draft',
    'released',
    'viewed',
    'accepted',
    'declined',
    'expired',
    'voided',
]);
export const estimatingEstimateTierSchema = z.enum(['standard', 'premium', 'complete']);
export const estimatingAcceptanceMethodSchema = z.enum(['customer', 'staff']);
export const estimatingCurrencyCodeSchema = z.string().regex(/^[A-Z]{3}$/);
export const estimatingMoneyCentsSchema = z.number().int().min(0).max(999_999_999_999);
export const estimatingQuantityTenThousandthsSchema = z.number().int().min(1).max(10_000_000);
export const estimatingBasisPointsSchema = z.number().int().min(0).max(10_000);
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
export const estimatingLineSourceReferenceSchema = z.strictObject({
    capability: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/),
    sourceId: z.string().trim().min(1).max(240),
    label: z.string().trim().min(1).max(240),
}).nullable();
export const estimatingEstimateLineInputSchema = z.strictObject({
    lineKey: z.string().uuid(),
    sourceReference: estimatingLineSourceReferenceSchema.default(null),
    groupLabel: nullableText(120).default(null),
    name: z.string().trim().min(1).max(240),
    description: nullableText(4_000).default(null),
    quantityTenThousandths: estimatingQuantityTenThousandthsSchema,
    unit: z.string().trim().min(1).max(60),
    unitPriceCents: estimatingMoneyCentsSchema,
    taxable: z.boolean().default(true),
    optional: z.boolean().default(false),
});
export const estimatingEstimateLineSchema = estimatingEstimateLineInputSchema.extend({
    lineNumber: z.number().int().positive(),
    lineTotalCents: estimatingMoneyCentsSchema,
});
export const estimatingEstimateTotalsSchema = z.strictObject({
    subtotalCents: estimatingMoneyCentsSchema,
    optionalTotalCents: estimatingMoneyCentsSchema,
    discountCents: estimatingMoneyCentsSchema,
    taxableAfterDiscountCents: estimatingMoneyCentsSchema,
    taxCents: estimatingMoneyCentsSchema,
    totalCents: estimatingMoneyCentsSchema,
});
export const estimatingEstimateDraftSchema = z.strictObject({
    projectId: z.string().uuid(),
    title: z.string().trim().min(1).max(240),
    tierCode: estimatingEstimateTierSchema.nullable().default(null),
    customerMessage: nullableText(8_000).default(null),
    internalNotes: nullableText(8_000).default(null),
    currencyCode: estimatingCurrencyCodeSchema.nullable().default(null),
    taxBasisPoints: estimatingBasisPointsSchema.nullable().default(null),
    discountBasisPoints: estimatingBasisPointsSchema.nullable().default(null),
    lines: z.array(estimatingEstimateLineInputSchema).min(1).max(500),
});
export const estimatingCreateEstimateCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: z.string().uuid(),
    estimateId: estimatingEstimateIdSchema,
    estimate: estimatingEstimateDraftSchema,
    origin: z.strictObject({
        kind: z.literal('estimate-template'),
        sourceTemplateId: z.string().uuid(),
        sourceTemplateVersionId: z.string().uuid(),
    }).optional(),
    reason: z.string().trim().min(10).max(500),
});
export const estimatingReviseEstimateCommandSchema = z.strictObject({
    kind: z.literal('revise'),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    estimate: estimatingEstimateDraftSchema,
    reason: z.string().trim().min(10).max(500),
});
const lifecycleCommandBase = {
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
};
export const estimatingReleaseEstimateCommandSchema = z.strictObject({
    kind: z.literal('release'),
    ...lifecycleCommandBase,
    expiresAt: z.string().datetime({ offset: true }),
});
export const estimatingAcceptEstimateCommandSchema = z.strictObject({
    kind: z.literal('accept'),
    ...lifecycleCommandBase,
    acceptance: z.strictObject({
        method: estimatingAcceptanceMethodSchema,
        acceptedByName: z.string().trim().min(1).max(240),
        acceptedAt: z.string().datetime({ offset: true }),
        note: nullableText(2_000).default(null),
        documentReference: z.strictObject({
            capability: z.literal('documents.instances.v1'),
            resourceId: z.string().uuid(),
            contentHash: z.string().regex(/^[a-f0-9]{64}$/),
        }).nullable().default(null),
    }),
});
export const estimatingSimpleLifecycleCommandSchema = z.strictObject({
    kind: z.enum(['view', 'decline', 'expire', 'void']),
    ...lifecycleCommandBase,
});
export const estimatingEstimateCommandSchema = z.discriminatedUnion('kind', [
    estimatingReviseEstimateCommandSchema,
    estimatingReleaseEstimateCommandSchema,
    estimatingAcceptEstimateCommandSchema,
    estimatingSimpleLifecycleCommandSchema,
]);
export const estimatingEstimateAcceptanceSchema = z.strictObject({
    id: z.string().uuid(),
    estimateId: estimatingEstimateIdSchema,
    versionId: z.string().uuid(),
    method: estimatingAcceptanceMethodSchema,
    acceptedByName: z.string().trim().min(1).max(240),
    acceptedAt: z.string().datetime({ offset: true }),
    note: nullableText(2_000),
    documentReference: z.unknown().nullable(),
    actorMembershipId: z.string().uuid(),
    actorPublicLinkId: z.string().uuid().nullable().default(null),
    createdAt: z.string().datetime({ offset: true }),
});
export const estimatingEstimateOriginSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('duplicate'),
        sourceEstimateId: estimatingEstimateIdSchema,
        sourceVersionId: z.string().uuid(),
        proposalPackageId: z.null(),
        tierCode: z.null(),
        sourceTemplateId: z.null(),
        sourceTemplateVersionId: z.null(),
    }),
    z.strictObject({
        kind: z.literal('proposal-package'),
        sourceEstimateId: estimatingEstimateIdSchema,
        sourceVersionId: z.string().uuid(),
        proposalPackageId: z.string().uuid(),
        tierCode: estimatingEstimateTierSchema,
        sourceTemplateId: z.null(),
        sourceTemplateVersionId: z.null(),
    }),
    z.strictObject({
        kind: z.literal('estimate-template'),
        sourceEstimateId: z.null(),
        sourceVersionId: z.null(),
        proposalPackageId: z.null(),
        tierCode: z.null(),
        sourceTemplateId: z.string().uuid(),
        sourceTemplateVersionId: z.string().uuid(),
    }),
]);
export const estimatingEstimatePublicDataSchema = z.strictObject({
    projectReference: projectsProjectReferenceSnapshotSchema,
    projectAccessDecision: projectsProjectAccessDecisionSchema,
    title: z.string().trim().min(1).max(240),
    tierCode: estimatingEstimateTierSchema.nullable(),
    customerMessage: nullableText(8_000),
    internalNotes: nullableText(8_000),
    currencyCode: estimatingCurrencyCodeSchema,
    taxBasisPoints: estimatingBasisPointsSchema,
    discountBasisPoints: estimatingBasisPointsSchema,
    totals: estimatingEstimateTotalsSchema,
    lines: z.array(estimatingEstimateLineSchema),
});
export const estimatingEstimateVersionSchema = z.strictObject({
    contractVersion: z.literal(estimatingEstimateVersionsCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    estimateId: estimatingEstimateIdSchema,
    versionNumber: z.number().int().positive(),
    status: estimatingEstimateStatusSchema,
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: z.string().trim().min(10).max(500),
    actorMembershipId: z.string().uuid().nullable(),
    actorType: z.enum(['membership', 'system-job', 'public-link']).default('membership'),
    actorSystemJobKey: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+$/)
        .nullable().default(null),
    actorPublicLinkId: z.string().uuid().nullable().default(null),
    createdAt: z.string().datetime({ offset: true }),
    data: estimatingEstimatePublicDataSchema,
}).superRefine((value, context) => {
    const membershipActor = value.actorType === 'membership' &&
        value.actorMembershipId !== null && value.actorSystemJobKey === null &&
        value.actorPublicLinkId === null;
    const systemActor = value.actorType === 'system-job' &&
        value.actorMembershipId === null && value.actorSystemJobKey !== null &&
        value.actorPublicLinkId === null;
    const publicLinkActor = value.actorType === 'public-link' &&
        value.actorMembershipId !== null && value.actorSystemJobKey === null &&
        value.actorPublicLinkId !== null;
    if (!membershipActor && !systemActor && !publicLinkActor) {
        context.addIssue({
            code: 'custom',
            path: ['actorType'],
            message: 'Estimate version must name exactly one membership, registered system-job, or public-link actor.',
        });
    }
});
export const estimatingEstimateSchema = z.strictObject({
    contractVersion: z.literal(estimatingEstimatesCapability),
    tenantId: z.string().uuid(),
    id: estimatingEstimateIdSchema,
    estimateNumber: z.string().trim().min(1).max(80),
    status: estimatingEstimateStatusSchema,
    revision: z.number().int().positive(),
    currentVersionId: z.string().uuid(),
    expiresAt: z.string().datetime({ offset: true }).nullable(),
    releasedAt: z.string().datetime({ offset: true }).nullable(),
    firstViewedAt: z.string().datetime({ offset: true }).nullable(),
    acceptedAt: z.string().datetime({ offset: true }).nullable(),
    declinedAt: z.string().datetime({ offset: true }).nullable(),
    expiredAt: z.string().datetime({ offset: true }).nullable(),
    voidedAt: z.string().datetime({ offset: true }).nullable(),
    acceptance: estimatingEstimateAcceptanceSchema.nullable(),
    origin: estimatingEstimateOriginSchema.nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    currentVersion: estimatingEstimateVersionSchema,
});
export const estimatingEstimateListQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    status: estimatingEstimateStatusSchema.nullable().default(null),
    tierCode: estimatingEstimateTierSchema.nullable().default(null),
    projectId: z.string().uuid().nullable().default(null),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
const estimatingPageSchema = z.strictObject({
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});
export const estimatingEstimateResponseSchema = z.strictObject({
    data: estimatingEstimateSchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
export const estimatingEstimatesResponseSchema = z.strictObject({
    data: z.array(estimatingEstimateSchema),
    page: estimatingPageSchema,
});
export const estimatingEstimateVersionsResponseSchema = z.strictObject({
    data: z.array(estimatingEstimateVersionSchema),
});
export const estimatingProjectSummaryResponseSchema = z.strictObject({
    data: z.strictObject({
        contractVersion: z.literal(estimatingProjectEstimateSummariesCapability),
        projectReference: projectsProjectReferenceSnapshotSchema,
        estimateCount: z.number().int().nonnegative(),
        activeCount: z.number().int().nonnegative(),
        acceptedCount: z.number().int().nonnegative(),
        voidedCount: z.number().int().nonnegative(),
        currencyCode: estimatingCurrencyCodeSchema.nullable(),
        draftTotalCents: estimatingMoneyCentsSchema.nullable(),
        releasedTotalCents: estimatingMoneyCentsSchema.nullable(),
        acceptedTotalCents: estimatingMoneyCentsSchema.nullable(),
        latestEstimate: z.strictObject({
            estimateId: estimatingEstimateIdSchema,
            estimateNumber: z.string().trim().min(1).max(80),
            status: estimatingEstimateStatusSchema,
            totalCents: estimatingMoneyCentsSchema,
            updatedAt: z.string().datetime({ offset: true }),
        }).nullable(),
    }),
});
function roundedRatio(numerator, denominator) {
    return (numerator + denominator / 2n) / denominator;
}
function safeCents(value) {
    if (value < 0n || value > 999999999999n) {
        throw new RangeError('Estimating money is outside the supported range.');
    }
    return Number(value);
}
export function calculateEstimatingLineTotal(quantityTenThousandths, unitPriceCents) {
    return safeCents(roundedRatio(BigInt(quantityTenThousandths) * BigInt(unitPriceCents), 10000n));
}
export function calculateEstimatingTotals(lines, taxBasisPoints, discountBasisPoints) {
    let subtotal = 0n;
    let optionalTotal = 0n;
    let taxableSubtotal = 0n;
    for (const line of lines) {
        const total = BigInt(calculateEstimatingLineTotal(line.quantityTenThousandths, line.unitPriceCents));
        if (line.optional) {
            optionalTotal += total;
        }
        else {
            subtotal += total;
            if (line.taxable)
                taxableSubtotal += total;
        }
    }
    const discount = roundedRatio(subtotal * BigInt(discountBasisPoints), 10000n);
    const afterDiscount = subtotal - discount;
    const taxableAfterDiscount = subtotal === 0n
        ? 0n
        : roundedRatio(taxableSubtotal * afterDiscount, subtotal);
    const tax = roundedRatio(taxableAfterDiscount * BigInt(taxBasisPoints), 10000n);
    return estimatingEstimateTotalsSchema.parse({
        subtotalCents: safeCents(subtotal),
        optionalTotalCents: safeCents(optionalTotal),
        discountCents: safeCents(discount),
        taxableAfterDiscountCents: safeCents(taxableAfterDiscount),
        taxCents: safeCents(tax),
        totalCents: safeCents(afterDiscount + tax),
    });
}
export function canTransitionEstimatingEstimate(current, target) {
    if (current === 'draft')
        return target === 'released' || target === 'voided';
    if (current === 'released') {
        return ['viewed', 'accepted', 'declined', 'expired', 'voided'].includes(target);
    }
    if (current === 'viewed') {
        return ['accepted', 'declined', 'expired', 'voided'].includes(target);
    }
    return current === 'accepted' && target === 'voided';
}
//# sourceMappingURL=estimating-estimate.contract.js.map